import { Component, HostListener, OnDestroy, OnInit, inject, DOCUMENT } from '@angular/core';
import {
  GridColumnV2,
  GridStateChangeV2,
  GridNoDataConfigV2,
  sortBy,
  StoreV2,
  GridStateV2,
  GridUtilsV2,
  deepClone,
  StorageV2Service,
  ActionMenuItemV2,
  ToastV2Service,
  ModalService,
  UserV2Service,
  NotificationsV2Module,
  ButtonV2Module,
  GridV2Module,
  ActionMenuV2Module,
} from '@schenck/rotec-components';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { isEqual } from 'lodash';
import { AsyncPipe } from '@angular/common';
import { UserGridModel, UserModel } from '../../models/user.model';
import { UserGridKeys, UserRole } from '../../enums/user.enum';
import { GRID_DATA_DEFAULT } from 'src/const/grid-config.const';
import { GridConfig } from 'src/models/grid-config.model';
import { UserService } from 'src/services/user.service';
import { GridConfigApi } from 'src/api/grid-config.api';
import { CreateEditUserModalComponent } from 'src/components/modals/create-edit-user-modal/create-edit-user-modal.component';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { SpinnerService } from 'src/services/spinner/spinner.service';
import {
  ConfirmModal,
  DeleteUserModelComponent,
} from 'src/components/modals/delete-user-modal/delete-user-modal.component';
import { ServerErrorService } from 'src/services/server-error/server-error.service';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';

const serviceName = 'UserGridService';

@Component({
  selector: 'um-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.less'],
  imports: [
    NotificationsV2Module,
    TranslatePipe,
    TranslateDirective,
    ButtonV2Module,
    GridV2Module,
    ActionMenuV2Module,
    AsyncPipe,
  ],
})
export class UserListGridComponent extends StoreV2<GridStateV2> implements OnInit, OnDestroy {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly gridConfigApi = inject(GridConfigApi);
  private readonly userService = inject(UserService);
  private readonly toastService = inject(ToastV2Service);
  private readonly modalService = inject(ModalService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly globalUserService = inject(UserV2Service);
  protected storageService: StorageV2Service;
  public serverErrorService = inject(ServerErrorService);

  public defaultFilters = {
    [UserGridKeys.Email]: GridUtilsV2.textFilter,
    [UserGridKeys.GivenName]: GridUtilsV2.textFilter,
    [UserGridKeys.Surname]: GridUtilsV2.textFilter,
    [UserGridKeys.Role]: GridUtilsV2.multiselectFilter,
  };
  public selectedMenuItems: ActionMenuItemV2[];
  public selectedGridRowId: String;

  public loading$ = this.spinnerService.spinnerActive$;

  public noDataText: GridNoDataConfigV2;
  public gridHeight: number;
  public hasPagination: boolean = false;

  public gridColumns: GridColumnV2[];
  public gridColumns$: Observable<GridColumnV2[]>;

  public users: UserGridModel[];
  public users$ = this.userService.users$;

  public gridConfig: GridConfig;
  public gridConfig$: BehaviorSubject<GridConfig>;

  public userGridParams: GridStateChangeV2;
  public userGridParams$: BehaviorSubject<GridStateChangeV2>;

  private _selectedUser: UserModel;

  private onDestroy$ = new Subject<void>();

  private _menuItems: ActionMenuItemV2[] = [
    {
      icon: 'edit',
      text: 'COMMON_Edit',
      onClick: () => this.openAddEditUserModal(this._selectedUser),
    },
    {
      icon: 'trash',
      text: 'COMMON_Delete',
      onClick: () => this.deleteUser(),
    },
  ];

  constructor() {
    const storageService = inject(StorageV2Service);

    super(deepClone(GRID_DATA_DEFAULT), serviceName, { name: serviceName, storageService });
    this.storageService = storageService;

    this.gridConfig = {
      pageSize: 15,
      displayedColumns: [],
      columnOrder: [],
      columnsWidths: {},
    };

    this.userGridParams = {
      page: this.state.page,
      filter: this.state.filter,
      sort: this.state.sort,
      fulltext: this.state.fulltext,
      type: this.state.type,
    };

    this.userGridParams$ = new BehaviorSubject(this.userGridParams);

    this.noDataText = {
      withFilter: {
        text: 'There are no entries for the selected filters. Remove or change filters to find entries.',
        buttonText: 'Remove all filters',
        buttonType: 'primary',
      },
      withoutFilter: {
        text: 'There are currently no entries. Click Add Entry to start the process of adding the first one.',
        buttonText: 'Add Entry',
        buttonType: 'primary',
      },
    };

    this.gridConfig = this.gridConfigApi.getGridConfig();

    this.gridConfigApi.gridConfig$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((gridConfig: GridConfig) => !!gridConfig)
      )
      .subscribe((gridConfig) => {
        const pageSizeChanged = this.gridConfig.pageSize !== gridConfig.pageSize;

        if (pageSizeChanged) {
          this.patchState(`${this.userGridParams.type}`, {
            ...this.userGridParams,
            pageSize: gridConfig.pageSize,
            total: this.users?.length,
            ...GridUtilsV2.getResults(this.users || [], {
              ...this.userGridParams,
              pageSize: gridConfig.pageSize,
              filterFnMap: this.defaultFilters,
              resultComparator: GridUtilsV2.defaultResultComparator,
            }),
            fulltext: this.userGridParams.fulltext,
            filter: this.userGridParams.filter,
            page: this.userGridParams.page,
          });
        }

        this.gridConfig = gridConfig;
      });

    combineLatest([this.userService.users$, this.userGridParams$])
      .pipe(
        takeUntil(this.onDestroy$),
        filter(([users]) => !!users)
      )
      .subscribe(([users, gridParams]) => {
        this.patchState(`${gridParams.type}`, {
          ...gridParams,
          pageSize: this.state.pageSize,
          total: users.length,
          ...GridUtilsV2.getResults(users || [], {
            ...gridParams,
            pageSize: this.state.pageSize,
            filterFnMap: this.defaultFilters,
            resultComparator: GridUtilsV2.defaultResultComparator,
          }),
          fulltext: gridParams.fulltext,
          filter: gridParams.filter,
          page: gridParams.page,
        });
      });

    this.userService.users$
      .pipe(
        takeUntil(this.onDestroy$),
        filter((users) => !!users)
      )
      .subscribe((users) => {
        this.users = users;
        this.setColumnDefinitions();
      });

    // Fetching initial Users
    this.userService.fetchUsers();
  }

  ngOnInit() {
    this.setGridHeight();

    this.state$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.hasPagination = this.state.total > this.state.pageSize;
      this.setGridHeight();
    });
  }

  public reactToColumnsChange(event: GridColumnV2[]) {
    const displayedColumns = [];
    const columnOrder = [];
    const columnsWidths = {};

    event.forEach((column) => {
      columnOrder.push(column.key);
      columnsWidths[column.key] = column.width;
      if (!column.invisible) {
        displayedColumns.push(column.key);
      }
    });

    const gridConfig = {
      ...this.gridConfig,
      displayedColumns,
      columnOrder,
      columnsWidths,
    };

    if (!isEqual(gridConfig, this.gridConfig)) {
      this.gridConfig = gridConfig;
      this.gridConfigApi.setGridConfig(gridConfig);
    }
  }

  public reactToStateChange(event: GridStateChangeV2): void {
    this.userGridParams = event;
    this.userGridParams$.next(event);
  }

  public reactToPageSizeChange(pageSize: number) {
    if (this.gridConfig.pageSize !== pageSize) {
      const gridConfig = { ...this.gridConfig, pageSize };
      this.userGridParams.page = 0;
      this.userGridParams$.next(this.userGridParams);
      this.gridConfigApi.setGridConfig(gridConfig);
    }
  }

  public setSelectedUser(user: UserModel, event: Event): void {
    event.stopPropagation();
    this._selectedUser = user;

    this.selectedMenuItems = this._menuItems.filter(({ icon }) => {
      switch (icon) {
        case 'trash':
          return this._selectedUser.email !== this.globalUserService.currentUser.email;
        default:
          return true;
      }
    });
  }

  public openAddEditUserModal(selectedUser?: UserModel): void {
    this.modalService
      .open(CreateEditUserModalComponent, selectedUser)
      .events$.pipe(
        filter((save: boolean) => !!save),
        take(1)
      )
      .subscribe(() => {
        this.userService.fetchUsers();
      });
  }

  public onRowClick(user: UserModel): void {
    if (this.selectedGridRowId === user.id) {
      this.selectedGridRowId = null;
    } else {
      this.selectedGridRowId = user.id;
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setColumnDefinitions() {
    this.gridColumns = [
      {
        key: UserGridKeys.Email,
        sortable: true,
        title: 'Email',
        width: '25%',
        staticWidth: false,
        filter: {
          type: 'text',
        },
      },
      {
        key: UserGridKeys.GivenName,
        sortable: true,
        title: 'First Name',
        width: '24%',
        staticWidth: false,
        filter: {
          type: 'text',
        },
      },
      {
        key: UserGridKeys.Surname,
        sortable: true,
        title: 'Last Name',
        width: '20%',
        filter: {
          type: 'text',
        },
      },
      {
        key: UserGridKeys.Role,
        sortable: true,
        title: 'Role',
        width: '20%',
        filter: {
          type: 'checkbox',
          options: Object.entries(UserRole).map(([_, val]) => {
            return {
              name: val,
              value: val,
            };
          }),
        },
      },
      {
        key: UserGridKeys.Actions,
        sortable: false,
        title: '',
        width: '10%',
        notReorderable: true,
        notResizable: true,
        notConfigurable: true,
        staticWidth: true,
      },
    ];

    const columns = this.gridColumns.map((col) => {
      const colKey = col.key;
      return <GridColumnV2>{
        ...col,
        invisible: !this.gridConfig.displayedColumns.includes(col.key),
        width: this.gridConfig.columnsWidths[colKey],
      };
    });

    this.gridColumns = sortBy(columns, (col) =>
      this.gridConfig.columnOrder.indexOf(<UserGridKeys>col.key)
    );
  }

  @HostListener('window:resize') public setGridHeight() {
    const pagesMain = this.document.querySelector('.pages-main');
    const navHeader = this.document.querySelector('.pages-navbar');
    const paginationHeight = this.hasPagination ? 80 : 0;

    this.gridHeight = pagesMain.clientHeight - navHeader.clientHeight - 200 - paginationHeight;
  }

  private deleteUser(): void {
    const modal = this.modalService.open(DeleteUserModelComponent, <ConfirmModal>{
      title: `Delete User NAME`,
      name: `${this._selectedUser.givenName} ${this._selectedUser.surname}`,
      text: `Do you really want to delete this user? All data will be lost.`,
    });
    modal.events$
      .pipe(
        filter((isDelete) => !!isDelete),
        switchMap(() => this.userService.deleteUser$(this._selectedUser)),
        catchError((err: HttpErrorResponse) => {
          this.toastService.openError(err?.message);
          throw Error(err?.message);
        })
      )
      .subscribe(() => {
        this.userService.fetchUsers();
        modal.close();
      });
  }
}
